import { set } from "lodash";
import React, { createContext, useContext, useState } from "react";

const SystemOutageContext = createContext();

export const useSystemOutage = () => {
  return useContext(SystemOutageContext);
};

export const SystemOutageProvider = ({ children }) => {
  const [displayBanner, setDisplayBanner] = useState(false); // Toggle to display banner
  const [bannerHeight, setBannerHeight] = useState(0);
  const [isNewsroomOpen, setIsNewsroomOpen] = useState(false);
  const [currentAppDown, setCurrentAppDown] = useState("default");

  // Configure the base paths for the different applications
  // values are arrays in case a web app on myapplied has multiple base paths
  // or to specify multiple default paths in case of a system outage
  const basePaths = {
    ecommerce: ["/applied-shop"],
    certInsurance: [
      "/resources/Certificates%20of%20Insurance%20and%20Workers%20Compensation",
    ],
    aiSearch: ["/ai/search"],
    aiLaunch: ["/"],
    all: ["*"],
    default: ["**N/A**"],
  };

  // Configure the pages where the banner should be displayed (if no pages are specified, set pagesToDisplayBanner to an empty array [])
  const pagesToDisplayBanner = [...basePaths[currentAppDown]];
  const aiLaunchMessage = (
    <span>
      Read our{" "}
      <a
        href="https://my.appliedmedical.com/resources/ATS---Corporate-AI/view/Maia%20User%20Guide/b93e31cc-0506-4363-9947-be904db00bdb"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "white",
          textDecoration: "underline",
          fontWeight: "bold",
        }}
      >
        User Guide
      </a>{" "}
      to learn more on how to use our AI assistant.
    </span>
  );

  const maiaOutageMessage = (
    <span>
      We are currently experiencing intermittent connectivity issues with{" "}
      <b>Maia</b> and are actively working to restore full service as quickly as
      possible.
      <br />
      If you need assistance, please submit a{" "}
      <a
        href="https://support.appliedmedical.com/sp"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "white",
          textDecoration: "underline",
          fontWeight: "bold",
        }}
      >
        support ticket
      </a>{" "}
      or call the Service Desk at <b>949-713-8500</b> and select <b>Option 1</b>
      .
    </span>
  );

  const ecommerceOutageMessage = (
    <span>
      We are having connection issues with our payment provider. You may
      continue to add items to the cart but will need to come back later to
      checkout. We appreciate your patience.
    </span>
  );

  const certInsuranceOutageMessage = (
    <span>
      Please refresh the page if Certificates of Insurance is not loading. The
      team is currently working on a fix."
    </span>
  );

  const defaultOutageMessage = (
    <span>
      We are currently experiencing a service outage. Our team is actively
      working to resolve the issue as quickly as possible. Thank you for your
      patience.
    </span>
  );

  const outageMessages = {
    ecommerce: ecommerceOutageMessage,
    certInsurance: certInsuranceOutageMessage,
    aiSearch: maiaOutageMessage,
    aiLaunch: aiLaunchMessage,
    all: defaultOutageMessage,
    default: defaultOutageMessage,
  };

  const outageTitles = {
    ecommerce: "Current Payment Connection Problems",
    aiSearch: "Service Advisory",
    default: "Service Outage",
    aiLaunch: "Maia is now live!",
  };

  // Configure the outage information
  const outageInfo = {
    title: outageTitles[currentAppDown],
    message: outageMessages[currentAppDown],
  };

  return (
    <SystemOutageContext.Provider
      value={{
        displayBanner,
        setDisplayBanner,
        bannerHeight,
        setBannerHeight,
        isNewsroomOpen,
        setIsNewsroomOpen,
        pagesToDisplayBanner,
        outageInfo,
        currentAppDown,
        setCurrentAppDown,
      }}
    >
      {children}
    </SystemOutageContext.Provider>
  );
};
